import React, { useState, useCallback } from 'react';
import { CheckCircle, XCircle, ArrowsClockwise, Lightning } from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CHECKS = [
  { id: 'auth', label: 'Login / Authentication', endpoint: '/api/auth/me', method: 'GET' },
  { id: 'companies', label: 'Company Management', endpoint: '/api/companies', method: 'GET' },
  { id: 'projects', label: 'Project Listing', endpoint: '/api/projects', method: 'GET' },
  { id: 'trades', label: 'Trade Configuration', endpoint: '/api/trades', method: 'GET' },
  { id: 'material_master', label: 'Material Master', endpoint: '/api/material-master', method: 'GET' },
  { id: 'vendor_supplier', label: 'Vendor / Supplier', endpoint: '/api/vendors', method: 'GET' },
  { id: 'notifications', label: 'Notification System', endpoint: '/api/notifications/unread-count', method: 'GET' },
  { id: 'cron_status', label: 'Cron Engine Status', endpoint: '/api/notifications/cron-status', method: 'GET' },
  { id: 'dashboard', label: 'Command Center Data', endpoint: '/api/dashboard/founder', method: 'GET' },
];

export const SmokeTest = () => {
  const [results, setResults] = useState({});
  const [running, setRunning] = useState(false);

  const runAll = useCallback(async () => {
    setRunning(true);
    const newResults = {};
    for (const check of CHECKS) {
      try {
        const start = performance.now();
        const res = await fetch(`${BACKEND_URL}${check.endpoint}`, {
          method: check.method,
          credentials: 'include',
        });
        const elapsed = Math.round(performance.now() - start);
        newResults[check.id] = {
          ok: res.ok,
          status: res.status,
          time: elapsed,
          detail: res.ok ? `${res.status} OK (${elapsed}ms)` : `${res.status} FAIL`,
        };
      } catch (e) {
        newResults[check.id] = { ok: false, status: 0, time: 0, detail: `Network error: ${e.message}` };
      }
    }
    setResults(newResults);
    setRunning(false);
  }, []);

  const passCount = Object.values(results).filter(r => r.ok).length;
  const failCount = Object.values(results).filter(r => !r.ok).length;
  const total = Object.keys(results).length;

  return (
    <div className="max-w-2xl mx-auto px-4 py-8" data-testid="smoke-test-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-lg font-bold text-zinc-900">System Health Check</h1>
          <p className="text-xs text-zinc-500 mt-0.5">Verify all modules are working correctly</p>
        </div>
        <button
          onClick={runAll}
          disabled={running}
          className="flex items-center gap-2 px-4 py-2 bg-[#1e3a5f] text-white text-sm font-medium rounded-lg hover:bg-[#152d4a] disabled:opacity-50 transition-colors"
          data-testid="run-all-checks"
        >
          {running ? (
            <><ArrowsClockwise size={16} className="animate-spin" /> Running...</>
          ) : (
            <><Lightning size={16} weight="fill" /> Run All Checks</>
          )}
        </button>
      </div>

      {total > 0 && (
        <div className="flex gap-3 mb-5">
          <div className="flex-1 text-center bg-green-50 border border-green-200 rounded-lg py-2">
            <span className="text-xl font-bold text-green-700">{passCount}</span>
            <span className="text-xs text-green-600 ml-1">Passed</span>
          </div>
          <div className="flex-1 text-center bg-red-50 border border-red-200 rounded-lg py-2">
            <span className="text-xl font-bold text-red-700">{failCount}</span>
            <span className="text-xs text-red-600 ml-1">Failed</span>
          </div>
          <div className="flex-1 text-center bg-zinc-50 border border-zinc-200 rounded-lg py-2">
            <span className="text-xl font-bold text-zinc-700">{total}</span>
            <span className="text-xs text-zinc-600 ml-1">Total</span>
          </div>
        </div>
      )}

      <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
        {CHECKS.map((check) => {
          const r = results[check.id];
          return (
            <div
              key={check.id}
              className={`flex items-center justify-between px-5 py-3.5 border-b border-zinc-100 last:border-b-0 ${
                r ? (r.ok ? 'bg-green-50/30' : 'bg-red-50/30') : ''
              }`}
              data-testid={`check-${check.id}`}
            >
              <div className="flex items-center gap-3">
                {r ? (
                  r.ok ? (
                    <CheckCircle size={20} weight="fill" className="text-green-500" />
                  ) : (
                    <XCircle size={20} weight="fill" className="text-red-500" />
                  )
                ) : (
                  <div className="w-5 h-5 rounded-full border-2 border-zinc-200" />
                )}
                <span className="text-sm font-medium text-zinc-700">{check.label}</span>
              </div>
              <div className="text-right">
                {r ? (
                  <span className={`text-xs font-mono ${r.ok ? 'text-green-600' : 'text-red-600'}`}>
                    {r.detail}
                  </span>
                ) : (
                  <span className="text-xs text-zinc-400">Not checked</span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <p className="text-center text-[10px] text-zinc-400 mt-6">
        Internal system verification. All checks use current session credentials.
      </p>
    </div>
  );
};
